import { useState } from 'react'
import { useMatch } from 'react-router-dom'
import { Menu, Moon, Sun, X } from 'lucide-react'
import { GlassButton } from '@/components/ui/glass-button'
import { track } from '@/lib/analytics'
import { cn } from '@/lib/utils'
import { navLinks, siteConfig } from '@/lib/site'
import { useTheme } from '@/lib/theme'
import { Wordmark } from '@shared/Wordmark'
import { demoHref } from '@features/prospect/demo'

export function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { theme, toggleTheme } = useTheme()
  // Prospect pages have one job: no nav, no portal link, just the demo CTA.
  const prospectSlug = useMatch('/for/:slug')?.params.slug
  const links = prospectSlug ? [] : navLinks

  return (
    <header className="sticky z-50" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
      <div className="mx-auto max-w-6xl px-4 pt-4">
        <div
          className={cn(
            'flex items-center justify-between gap-4 rounded-2xl border px-4 py-3',
            'border-black/10 bg-white/50 backdrop-blur-xl',
            'dark:border-white/[0.08] dark:bg-white/[0.08]'
          )}
        >
          <a href="/" className="flex items-center gap-2" aria-label={`${siteConfig.name} home`}>
            <Wordmark decorative />
          </a>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-black/5 hover:text-foreground dark:hover:bg-white/10"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <GlassButton
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            >
              {theme === 'dark' ? <Sun /> : <Moon />}
            </GlassButton>

            {prospectSlug ? (
              <GlassButton variant="primary" size="sm" asChild className="hidden sm:inline-flex">
                <a
                  href={demoHref(prospectSlug)}
                  onClick={() =>
                    track('demo_cta_clicked', { slug: prospectSlug, placement: 'header' })
                  }
                >
                  Book a demo
                </a>
              </GlassButton>
            ) : (
              <GlassButton variant="primary" size="sm" asChild className="hidden sm:inline-flex">
                <a href={siteConfig.portalUrl}>Open portal</a>
              </GlassButton>
            )}

            <GlassButton
              variant="ghost"
              size="icon"
              className={cn('md:hidden', prospectSlug && 'hidden')}
              onClick={() => setMobileOpen((open) => !open)}
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? <X /> : <Menu />}
            </GlassButton>
          </div>
        </div>

        <nav
          hidden={!mobileOpen}
          aria-label="Mobile"
          className={cn(
            'mt-2 flex flex-col gap-1 rounded-2xl border p-2 md:hidden',
            'border-black/10 bg-white/60 backdrop-blur-xl',
            'dark:border-white/[0.08] dark:bg-white/[0.10]'
          )}
        >
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-black/5 hover:text-foreground dark:hover:bg-white/10"
            >
              {link.label}
            </a>
          ))}
          <a
            href={siteConfig.portalUrl}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-foreground"
          >
            Open portal
          </a>
        </nav>
      </div>
    </header>
  )
}
